import {
    Autocomplete,
    Box,
    Chip,
    TextField,
    Typography,
} from "@mui/material";
import React, { useState } from "react";

import { monospaceStyle } from "../../theme";
import { FlagRule } from "../../types/valueChain";
import { FLAG_WORDS, countryName, ruleFlagOf } from "./chainLens";
import {
    DOMESTIC_COLOUR,
    FlagChip,
    OTHER_COLOUR,
    TRUSTED_COLOUR,
} from "./LensBlocks";
import { FLAG_RULES, toCountryCode, toTrustedSet } from "./valueChainState";

// What the settings say of a flag, under the swatches and on every report
export const FLAG_CAPTION =
    "A flag means nothing is on record, not that nothing exists. The evidence base sits on every row of the report.";

interface JurisdictionSettingsProps {
    // The home jurisdiction; null until one is set
    home: string | null;
    onHomeChange: (home: string | null) => void;
    // The countries trusted beside it: a bucket of their own, never counted
    // as domestic
    trusted: string[];
    onTrustedChange: (trusted: string[]) => void;
    rule: FlagRule;
    onRuleChange: (rule: FlagRule) => void;
    // The country codes the makers of the chain are at home in, offered
    // first
    countries: string[];
}

// A country as typed or picked: a code, or a name the options know
const resolveCountry = (
    value: string,
    options: readonly string[],
): string | null => {
    const code = toCountryCode(value);
    if (code) {
        return code;
    }
    const wanted = value.trim().toLowerCase();
    return (
        options.find((option) => countryName(option).toLowerCase() === wanted) ??
        null
    );
};

// The small upper-case label of a setting
const SettingLabel: React.FC<{
    htmlFor: string;
    children: React.ReactNode;
}> = ({ htmlFor, children }) => (
    <Typography
        component="label"
        htmlFor={htmlFor}
        sx={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "text.secondary",
        }}
    >
        {children}
    </Typography>
);

// What a lowercase "optional" beside an upper-case label looks like
const Optional: React.FC = () => (
    <Box
        component="span"
        sx={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}
    >
        {" "}
        · optional
    </Box>
);

// The option of a country list, its own key kept out of the spread props
const renderCountry = (
    props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: string,
) => {
    const { key: _key, ...rest } = props;
    return (
        <li {...rest} key={option}>
            <CountryOption code={option} />
        </li>
    );
};

// One country as an option: its name, its code beside it
const CountryOption: React.FC<{ code: string }> = ({ code }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            width: "100%",
        }}
    >
        <span>{countryName(code)}</span>
        <Box
            component="span"
            sx={{ ...monospaceStyle, color: "text.disabled" }}
        >
            {code}
        </Box>
    </Box>
);

const Swatch: React.FC<{ sx: Record<string, unknown> }> = ({ sx }) => (
    <Box
        component="span"
        sx={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "2px",
            boxSizing: "border-box",
            flexShrink: 0,
            ...sx,
        }}
    />
);

const SELECT_SX = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "background.raised",
        fontSize: 13,
    },
} as const;

/**
 * The settings of the jurisdiction lens, beside the chain: the home
 * jurisdiction, the trusted set the analyst defines, and when a kind is
 * flagged, with what the colours and the flag mean. All three settings live
 * in the address.
 */
const JurisdictionSettings: React.FC<JurisdictionSettingsProps> = ({
    home,
    onHomeChange,
    trusted,
    onTrustedChange,
    rule,
    onRuleChange,
    countries,
}) => {
    const [homeError, setHomeError] = useState(false);
    const homeOptions = Array.from(
        new Set([...(home ? [home] : []), ...countries]),
    );
    const trustedOptions = countries.filter((code) => code !== home);
    const ruleWords = (option: FlagRule) =>
        FLAG_WORDS[ruleFlagOf(option, trusted.length > 0)];

    return (
        <Box
            component="aside"
            aria-label="Jurisdiction settings"
            sx={{
                width: { xs: "100%", md: 260 },
                flexShrink: 0,
                alignSelf: "flex-start",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                p: "14px 16px 16px",
                border: 1,
                borderColor: "divider",
                borderRadius: (theme) => `${theme.radius.surface}px`,
                backgroundColor: "background.paper",
            }}
        >
            <Typography
                component="h2"
                sx={{ m: 0, fontSize: "13.5px", fontWeight: 600 }}
            >
                Jurisdiction lens
            </Typography>

            {!home && (
                <Typography variant="caption" color="warning.main" role="note">
                    Set a home jurisdiction to split every card's makers by
                    country.
                </Typography>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <SettingLabel htmlFor="value-chain-home">
                    Home jurisdiction
                </SettingLabel>
                <Autocomplete
                    id="value-chain-home"
                    freeSolo
                    size="small"
                    options={homeOptions}
                    value={home}
                    getOptionLabel={countryName}
                    renderOption={renderCountry}
                    onInputChange={() => setHomeError(false)}
                    onChange={(_event, value) => {
                        if (value === null || value.trim() === "") {
                            setHomeError(false);
                            if (home !== null) {
                                onHomeChange(null);
                            }
                            return;
                        }
                        const code = resolveCountry(value, homeOptions);
                        setHomeError(code === null);
                        if (code && code !== home) {
                            onHomeChange(code);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Not set"
                            error={homeError}
                            helperText={
                                homeError
                                    ? "A country code (two or three letters) or a country of the chain"
                                    : undefined
                            }
                        />
                    )}
                    sx={SELECT_SX}
                />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <SettingLabel htmlFor="value-chain-trusted">
                    Trusted set
                    <Optional />
                </SettingLabel>
                <Autocomplete
                    id="value-chain-trusted"
                    multiple
                    freeSolo
                    size="small"
                    options={trustedOptions}
                    value={trusted}
                    getOptionLabel={countryName}
                    renderOption={renderCountry}
                    onChange={(_event, values) =>
                        onTrustedChange(
                            toTrustedSet(
                                values.map(
                                    (value) =>
                                        resolveCountry(value, trustedOptions) ??
                                        "",
                                ),
                            ),
                        )
                    }
                    renderTags={(values, getTagProps) =>
                        values.map((code, index) => {
                            const { key: _key, ...tagProps } = getTagProps({
                                index,
                            });
                            return (
                            <Chip
                                {...tagProps}
                                key={code}
                                label={code}
                                size="small"
                                title={countryName(code)}
                                deleteIcon={
                                    <span aria-label={`Remove ${code}`}>×</span>
                                }
                                sx={{ height: 20, ...monospaceStyle }}
                            />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={
                                trusted.length === 0 ? "Not configured" : undefined
                            }
                        />
                    )}
                    sx={SELECT_SX}
                />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <SettingLabel htmlFor="value-chain-rule">
                    Flag a kind when
                </SettingLabel>
                <TextField
                    select
                    id="value-chain-rule"
                    size="small"
                    value={rule}
                    onChange={(event) => {
                        const picked = FLAG_RULES.find(
                            (option) => option === event.target.value,
                        );
                        if (picked) {
                            onRuleChange(picked);
                        }
                    }}
                    SelectProps={{ native: true }}
                    sx={SELECT_SX}
                >
                    {FLAG_RULES.map((option) => (
                        <option key={option} value={option}>
                            {ruleWords(option)}
                        </option>
                    ))}
                </TextField>
            </Box>

            <Box
                component="ul"
                aria-label="What the colours mean"
                sx={{
                    m: 0,
                    mt: 0.5,
                    p: 0,
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    fontSize: "12.5px",
                    color: "text.secondary",
                    "& li": { display: "flex", alignItems: "center", gap: 0.75 },
                }}
            >
                <li>
                    <Swatch sx={{ backgroundColor: DOMESTIC_COLOUR }} />
                    domestic
                </li>
                <li>
                    <Swatch sx={{ backgroundColor: TRUSTED_COLOUR }} />
                    trusted: a bucket of its own, never domestic
                </li>
                <li>
                    <Swatch sx={{ backgroundColor: OTHER_COLOUR }} />
                    other country on record
                </li>
                <li>
                    <Swatch sx={{ border: `1px solid ${OTHER_COLOUR}` }} />
                    country unknown
                </li>
                <li>
                    <FlagChip flag="no_domestic_maker" label="flag" />
                    nothing domestic on record
                </li>
            </Box>

            <Typography variant="caption" sx={{ color: "text.disabled" }}>
                {FLAG_CAPTION}
            </Typography>
        </Box>
    );
};

export default JurisdictionSettings;
